import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import { hasFeature } from "../utils/featureAccess";
import { sessionManager, Subscription } from "../services/sessionManager.service";
import { User } from "../types/auth.types";

interface AuthContextType {
  token: string | null;
  restaurantId: number;
  isAuthenticated: boolean;
  availableFeatures: number[];
  includedModules: string[];
  loading: boolean;
  isInitialized: boolean;
  user: User | null;
  subscription: Subscription | null;
  checkFeature: (featureCode: number) => boolean;
  hasModule: (moduleKey: string) => boolean;
  login: (
    token: string,
    restaurantId?: number,
    features?: number[],
    modules?: string[],
    skipProfileFetch?: boolean,
  ) => void;
  logout: () => void;
  setRestaurantId: (id: number) => void;
  setAvailableFeatures: (features: number[]) => void;
  setIncludedModules: (modules: string[]) => void;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Simplified authentication context using SessionManager
 *
 * This is now a thin wrapper around SessionManager that exposes
 * authentication state to React components via Context API.
 * All business logic has been moved to SessionManager service.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [restaurantId, setRestaurantIdState] = useState<number>(1);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [availableFeatures, setAvailableFeaturesState] = useState<number[]>([]);
  const [includedModules, setIncludedModulesState] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  // Initialize SessionManager on mount and subscribe to state changes
  useEffect(() => {
    // Subscribe to SessionManager state updates
    const unsubscribe = sessionManager.subscribe((state) => {
      setToken(state.token);
      setIsAuthenticated(state.isAuthenticated);
      setUser(state.user);
      setRestaurantIdState(state.restaurantId);
      setAvailableFeaturesState(state.availableFeatures);
      setIncludedModulesState(state.includedModules);
      setLoading(state.loading);
      setIsInitialized(state.isInitialized);
      setSubscription(state.subscription);
    });

    // Initialize session (load from cache or fetch from API)
    sessionManager.initialize();

    return () => {
      unsubscribe();
    };
  }, []);

  const login = (
    newToken: string,
    newRestaurantId?: number,
    features?: number[],
    modules?: string[],
    skipProfileFetch?: boolean,
  ) => {
    // Delegate to SessionManager - setSessionFromProfile already persists the
    // session and triggers a background refresh. Do NOT call initialize() here
    // as that would cause a duplicate /api/auth/user-profile request.
    sessionManager.setSessionFromProfile({
      success: true,
      message: "Login successful",
      token: newToken,
      user: user as any, // Will be updated by background refresh
      restaurant: newRestaurantId
        ? ({ id: newRestaurantId } as any)
        : undefined,
      access: {
        permissions: [],
        modules: modules || [],
        isOwner: false,
        isAdmin: false,
        isSuperAdmin: false,
        hasAllLocations: false,
      },
      tenant: null as any,
      subscription: null as any,
    });
  };

  const logout = () => {
    sessionManager.clearSession();
  };

  const setRestaurantId = (id: number) => {
    setRestaurantIdState(id);
    // Store in all possible keys for compatibility
    localStorage.setItem("businessId", id.toString());
    localStorage.setItem("restaurantId", id.toString());
    localStorage.setItem("loyaltyRestaurantId", id.toString());
  };

  const setAvailableFeatures = (features: number[]) => {
    setAvailableFeaturesState(features);
    localStorage.setItem("availableFeatures", JSON.stringify(features));
  };

  const setIncludedModules = (modules: string[]) => {
    setIncludedModulesState(modules);
    localStorage.setItem("includedModules", JSON.stringify(modules));
  };

  const refreshPermissions = async () => {
    await sessionManager.initialize();
  };

  const checkFeature = useCallback(
    (featureCode: number): boolean => {
      return hasFeature(featureCode, availableFeatures);
    },
    [availableFeatures],
  );

  const hasModule = useCallback(
    (moduleKey: string): boolean => {
      return includedModules.includes(moduleKey);
    },
    [includedModules],
  );

  const value = useMemo(
    () => ({
      token,
      restaurantId,
      isAuthenticated,
      availableFeatures,
      includedModules,
      loading,
      isInitialized,
      user,
      subscription,
      checkFeature,
      hasModule,
      login,
      logout,
      setRestaurantId,
      setAvailableFeatures,
      setIncludedModules,
      refreshPermissions,
    }),
    [
      token,
      restaurantId,
      isAuthenticated,
      availableFeatures,
      includedModules,
      loading,
      isInitialized,
      user,
      subscription,
      checkFeature,
      hasModule,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// For development/demo purposes, provide a default mock token
export const DEV_TOKEN = "dev-token-12345";
export const DEV_RESTAURANT_ID = 1;
